import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, RefreshCw, Phone, RotateCcw, LogOut, ClipboardList } from "lucide-react";
import { BUSINESS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
};

const playAlert = (ctx) => {
  if (!ctx) return;
  const beep = (t) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.6, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.4);
  };
  const now = ctx.currentTime;
  [0, 0.5, 1.0, 1.5].forEach((d) => {
    beep(now + d);
    beep(now + d + 0.2);
  });
};

export default function StaffPage() {
  const [pin, setPin] = useState(() => sessionStorage.getItem("staffPin") || "");
  const [pinInput, setPinInput] = useState("");
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("new");
  const [refreshing, setRefreshing] = useState(false);
  const knownIdsRef = useRef(null);
  const audioCtxRef = useRef(null);

  const load = useCallback(
    async (p) => {
      const activePin = p || pin;
      if (!activePin) return;
      setRefreshing(true);
      try {
        const res = await fetch(`${API}/orders`, { headers: { "X-Staff-Pin": activePin } });
        if (res.status === 401) {
          sessionStorage.removeItem("staffPin");
          setPin("");
          setError("Incorrect PIN. Please try again.");
          return;
        }
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (knownIdsRef.current !== null) {
          const fresh = data.filter((o) => !knownIdsRef.current.has(o.id) && o.status !== "done");
          if (fresh.length > 0) playAlert(audioCtxRef.current);
        }
        knownIdsRef.current = new Set(data.map((o) => o.id));
        setOrders(data);
        setError("");
      } catch {
        setError("Couldn't load orders. Try refreshing.");
      } finally {
        setRefreshing(false);
      }
    },
    [pin]
  );

  useEffect(() => {
    if (!pin) return;
    load();
    const t = setInterval(() => load(), 15000);
    return () => clearInterval(t);
  }, [pin, load]);

  const submitPin = (e) => {
    e.preventDefault();
    const p = pinInput.trim();
    if (!p) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      audioCtxRef.current.resume();
    } catch {}
    sessionStorage.setItem("staffPin", p);
    setPin(p);
  };

  const setStatus = async (order, status) => {
    await fetch(`${API}/orders/${order.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-Staff-Pin": pin },
      body: JSON.stringify({ status }),
    });
    load();
  };

  const logout = () => {
    sessionStorage.removeItem("staffPin");
    setPin("");
    setOrders([]);
  };

  if (!pin) {
    return (
      <div data-testid="staff-login" className="flex min-h-screen items-center justify-center bg-charcoal px-5">
        <form onSubmit={submitPin} className="w-full max-w-sm border border-cream/15 bg-[#1f1f23] p-8 text-center">
          <img src="/logo-badge.jpg" alt="Croudace Pizza & Burger" className="mx-auto h-16 w-16 rounded-full object-cover" />
          <p className="mt-5 font-display text-2xl uppercase tracking-wide text-cream">Staff Orders</p>
          <p className="mt-1 text-xs uppercase tracking-[0.25em] text-cream/50">Enter your staff PIN</p>
          <input
            data-testid="staff-pin-input"
            type="password"
            inputMode="numeric"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            placeholder="PIN"
            className="mt-6 w-full border border-cream/20 bg-charcoal px-4 py-3 text-center text-lg tracking-[0.4em] text-cream placeholder:text-cream/30 focus:border-ember focus:outline-none"
          />
          {error && <p data-testid="staff-pin-error" className="mt-3 text-xs font-semibold text-red-400">{error}</p>}
          <button
            data-testid="staff-login-button"
            type="submit"
            className="mt-5 w-full rounded-full bg-ember px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-cream transition-colors hover:bg-[#991B1B]"
          >
            View Orders
          </button>
        </form>
      </div>
    );
  }

  const newOrders = orders.filter((o) => o.status !== "done");
  const doneOrders = orders.filter((o) => o.status === "done");
  const visible = tab === "done" ? doneOrders : newOrders;

  return (
    <div data-testid="staff-page" className="min-h-screen bg-cream text-charcoal">
      <header className="sticky top-0 z-40 border-b border-black/10 bg-cream/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4">
          <Link to="/" data-testid="staff-back-link" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-charcoal/60 transition-colors hover:text-ember">
            <ArrowLeft size={15} /> Site
          </Link>
          <p className="flex items-center gap-3">
            <img src="/logo-badge.jpg" alt="" className="h-9 w-9 rounded-full object-cover" />
            <span className="font-display text-lg uppercase tracking-wide">Staff Orders</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              data-testid="staff-refresh-button"
              onClick={() => load()}
              aria-label="Refresh orders"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-charcoal/20 text-charcoal/60 transition-colors hover:border-ember hover:text-ember"
            >
              <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="staff-logout-button"
              onClick={logout}
              aria-label="Log out"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-charcoal/20 text-charcoal/60 transition-colors hover:border-ember hover:text-ember"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-8">
        <div className="flex gap-2">
          <button
            data-testid="staff-tab-new"
            onClick={() => setTab("new")}
            className={`rounded-full border px-6 py-2.5 text-xs font-bold uppercase tracking-widest transition-all ${
              tab === "new" ? "border-ember bg-ember text-cream" : "border-charcoal/20 text-charcoal/60"
            }`}
          >
            New ({newOrders.length})
          </button>
          <button
            data-testid="staff-tab-done"
            onClick={() => setTab("done")}
            className={`rounded-full border px-6 py-2.5 text-xs font-bold uppercase tracking-widest transition-all ${
              tab === "done" ? "border-ember bg-ember text-cream" : "border-charcoal/20 text-charcoal/60"
            }`}
          >
            Done ({doneOrders.length})
          </button>
        </div>

        {error && <p data-testid="staff-error" className="mt-4 text-sm font-semibold text-ember">{error}</p>}

        {visible.length === 0 ? (
          <div data-testid="staff-empty" className="mt-20 flex flex-col items-center gap-3 text-center">
            <ClipboardList size={44} className="text-charcoal/20" />
            <p className="font-display text-xl uppercase tracking-wide text-charcoal/50">
              {tab === "new" ? "No new orders" : "No completed orders"}
            </p>
            <p className="text-sm text-charcoal/40">This page refreshes automatically every 15 seconds.</p>
          </div>
        ) : (
          <ul className="mt-6 space-y-4">
            {visible.map((o) => (
              <li
                key={o.id}
                data-testid={`staff-order-${o.order_number}`}
                className={`border bg-white/60 p-5 ${o.status === "done" ? "border-black/10 opacity-60" : "border-l-4 border-l-ember border-black/10"}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-4">
                    <span className="font-display text-2xl tracking-[0.15em] text-ember">#{o.order_number}</span>
                    <span className="text-xs uppercase tracking-widest text-charcoal/40">{fmtTime(o.created_at)}</span>
                  </div>
                  <span data-testid={`staff-pickup-${o.order_number}`} className="rounded-full bg-charcoal px-4 py-1.5 text-[0.65rem] font-bold uppercase tracking-widest text-cream">
                    Pickup: {o.pickup_time}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                  <span className="font-semibold">{o.customer_name}</span>
                  <a href={`tel:${o.phone}`} data-testid={`staff-phone-${o.order_number}`} className="flex items-center gap-1.5 text-ember transition-colors hover:text-[#991B1B]">
                    <Phone size={13} /> {o.phone}
                  </a>
                </div>
                <ul className="mt-3 space-y-1.5 border-t border-black/10 pt-3">
                  {o.items.map((i, idx) => (
                    <li key={idx} className="text-sm">
                      <span className="font-medium">
                        {i.name} <span className="text-charcoal/40">x {i.qty}</span>
                      </span>
                      {i.selections && <span className="block text-xs font-medium text-ember/80">{i.selections}</span>}
                    </li>
                  ))}
                </ul>
                {o.notes && (
                  <p data-testid={`staff-notes-${o.order_number}`} className="mt-3 border border-dashed border-ember/50 bg-ember/5 px-3 py-2 text-xs font-medium text-charcoal/70">
                    Notes: {o.notes}
                  </p>
                )}
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-display text-xl text-charcoal">${o.total.toFixed(2)}</span>
                  {o.status === "done" ? (
                    <button
                      data-testid={`staff-reopen-${o.order_number}`}
                      onClick={() => setStatus(o, "new")}
                      className="flex items-center gap-2 rounded-full border border-charcoal/25 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-charcoal/60 transition-colors hover:border-ember hover:text-ember"
                    >
                      <RotateCcw size={13} /> Reopen
                    </button>
                  ) : (
                    <button
                      data-testid={`staff-done-${o.order_number}`}
                      onClick={() => setStatus(o, "done")}
                      className="flex items-center gap-2 rounded-full bg-ember px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-cream transition-colors hover:bg-[#991B1B]"
                    >
                      <Check size={13} /> Mark Done
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-10 text-center text-[0.65rem] uppercase tracking-[0.25em] text-charcoal/35">
          {BUSINESS.name} · Staff use only
        </p>
      </main>
    </div>
  );
}
